import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getUpcoming, searchLife } from '../lifeos/api.js';
import { daysLeftLabel, humanize, money } from '../lifeos/format.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import { ErrorState, Loading } from '../lifeos/ui.js';
import { useLoad } from '../lifeos/useLoad.js';
import type { SearchResult, Sphere, UpcomingItem } from '../lifeos/types.js';
import { LIBRARY } from '../nav.js';
import { upcomingHref } from '../lifeos/links.js';
import { EmptyState, Group, IconTile, MoreRow, Meta, Page, Row, SearchField, TypeTile, When } from '../ui/index.js';

export { upcomingHref };

/** Where a search hit lives in the app. Ids are always strings in mixed payloads. */
export function searchHref(r: SearchResult): string {
  const q = encodeURIComponent(r.title);
  switch (r.type) {
    case 'task':
      return `/tasks?q=${q}`;
    case 'person':
      return `/people/${encodeURIComponent(r.id)}`;
    case 'org':
      return `/people?org=${encodeURIComponent(r.id)}`;
    case 'project':
      return `/projects/${encodeURIComponent(r.id)}`;
    case 'trip':
      return `/trips/${encodeURIComponent(r.id)}`;
    case 'life_note':
      return `/notes?q=${q}`;
    case 'message':
      return '/inbox?status=new';
    case 'obligation':
      return '/obligations';
    case 'service':
      return '/services';
    case 'document':
      return '/documents';
    case 'belonging':
      return '/belongings';
  }
}

/**
 * What the More hub's "coming up" list shows: bills, renewals, birthdays and
 * events — not tasks, which already live in Tasks and on Today.
 */
export function libraryUpcoming(items: readonly UpcomingItem[]): UpcomingItem[] {
  return items.filter((u) => u.type !== 'task');
}

/** Coming-up rows shown before "Show N more". */
export const COMING_SHOWN = 5;

/**
 * More (`/library`): everything off the four-item nav. Search first, then
 * what's coming up (30 days; `?days=7` is the week Today's count opens),
 * then one Settings-style group per section.
 */
export default function Library(): JSX.Element {
  const [params] = useSearchParams();
  const days = params.get('days') === '7' ? 7 : 30;
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const spheres = useSpheres();
  const upcoming = useLoad(() => getUpcoming(days), [days]);
  const coming = upcoming.data ? libraryUpcoming(upcoming.data) : null;
  const [all, setAll] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const shown = coming ? (all ? coming : coming.slice(0, COMING_SHOWN)) : [];

  return (
    <Page title="More" className="la">
      <SearchField value={query} onChange={setQuery} placeholder="Search people, tasks, notes…" label="Search everything" />

      {debounced.length >= 2 ? (
        <SearchResults q={debounced} spheres={spheres} />
      ) : (
        <>
          {upcoming.error ? (
            <ErrorState message={upcoming.error} onRetry={upcoming.reload} />
          ) : !coming ? (
            <Loading rows={2} />
          ) : (
            <Group header={days === 7 ? 'This week' : 'Coming up'} label="Coming up" className="la-coming">
              {shown.map((u) => (
                <Row
                  key={`${u.type}:${u.id}:${u.dueOn}`}
                  to={upcomingHref(u)}
                  leading={<TypeTile type={u.type} />}
                  title={u.title}
                  sub={
                    <Meta color={sphereColor(spheres, u.sphere)}>
                      {humanize(u.type)}
                      {u.amountCents !== undefined && ` · ${money(u.amountCents)}`}
                    </Meta>
                  }
                  detail={<When tone={u.daysLeft < 0 ? 'overdue' : u.daysLeft === 0 ? 'today' : 'later'}>{daysLeftLabel(u.daysLeft)}</When>}
                />
              ))}
              {coming.length > shown.length && <MoreRow count={coming.length - shown.length} onClick={() => setAll(true)} />}
            </Group>
          )}

          {LIBRARY.map((section) => (
            <Group key={section.title} header={section.title} label={section.title} className="la-nav">
              {section.links.map((l) => (
                <Row key={l.to} to={l.to} leading={<IconTile name={l.icon} color={l.tint} />} title={l.label} />
              ))}
            </Group>
          ))}
        </>
      )}
    </Page>
  );
}

function SearchResults({ q, spheres }: { q: string; spheres: Sphere[] }): JSX.Element {
  const results = useLoad(() => searchLife(q), [q]);
  if (results.error) return <ErrorState message={results.error} onRetry={results.reload} />;
  if (!results.data) return <Loading rows={3} />;
  if (results.data.length === 0) return <EmptyState icon="search" title={`Nothing matches “${q}”.`} />;
  return (
    <Group label="Search results">
      {results.data.map((r) => (
        <Row
          key={`${r.type}:${r.id}`}
          to={searchHref(r)}
          leading={<TypeTile type={r.type} />}
          title={r.title}
          sub={
            <Meta color={sphereColor(spheres, r.sphere)}>
              {humanize(r.type)}
              {r.snippet && <span className="la-snippet"> · {r.snippet}</span>}
            </Meta>
          }
        />
      ))}
    </Group>
  );
}
